/* ============================================================================
   ACEPLORE · module-ai.js — inbuilt AI shopping assistant (<lumina-ai>).
   Providers: 'proxy' (recommended), 'anthropic'/'openai' (dev key via
   localStorage 'lumina:ai_key' only), 'rules' (no API; catalog-grounded).
   Always grounds recommendations in real store products via the Predictive
   Search API so it never invents products or prices.
   ========================================================================== */
(function () {
  'use strict';
  const { utils, config } = window.Lumina;

  class LuminaAI extends window.Lumina.Element {
    connectedCallback() {
      this.provider = this.dataset.provider || 'rules';
      this.proxyUrl = this.dataset.proxy || '';
      this.model = this.dataset.model || '';
      this.name = this.dataset.name || 'Assistant';
      // Merchant setting: "Enable AI product recommendations".
      this.recommend = this.dataset.recommend !== 'false';
      this.cfg = JSON.parse(this.querySelector('[data-ai-config]').textContent || '{}');
      this.messagesEl = this.querySelector('[data-ai-messages]');
      this.form = this.querySelector('[data-ai-form]');
      this.input = this.querySelector('[data-ai-input]');
      this.panel = this.querySelector('.lm-ai__panel');
      this.overlay = this.querySelector('[data-ai-overlay]');
      this.suggestionsEl = this.querySelector('[data-ai-suggestions]');
      this.history = [];
      this.release = null;
      this.opened = false;

      this.listen(document, 'click', (e) => { if (e.target.closest('[data-ai-open]')) { e.preventDefault(); this.open(); } });
      this.querySelectorAll('[data-ai-close]').forEach((b) => this.listen(b, 'click', () => this.close()));
      this.listen(this.overlay, 'click', () => this.close());
      this.listen(document, 'keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
      this.listen(this.form, 'submit', (e) => { e.preventDefault(); this.send(this.input.value); });
      this.listen(this.input, 'input', () => { this.input.style.height = 'auto'; this.input.style.height = Math.min(120, this.input.scrollHeight) + 'px'; });
      this.listen(this.input, 'keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(this.input.value); } });
    }

    open() {
      if (this.isOpen) { this.input.focus(); return; }
      this.isOpen = true;
      this.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      this.panel.removeAttribute('inert');
      this.panel.setAttribute('aria-hidden', 'false');
      this.querySelectorAll('[data-ai-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
      if (window.matchMedia('(max-width: 749px)').matches) { utils.lockScroll(true); this._locked = true; }
      if (!this.opened) { this.opened = true; this.greet(); }
      this.release = utils.trapFocus(this.panel, document.activeElement);
      setTimeout(() => this.input.focus(), 60);
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      if (this._locked) { utils.lockScroll(false); this._locked = false; }
      this.release && this.release();
      this.release = null;
      this.overlay.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('inert', '');
      this.querySelectorAll('[data-ai-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    }

    greet() {
      this.addMessage('assistant', this.cfg.greeting || `Hi! I'm ${this.name}.`);
      this.renderSuggestions(this.cfg.suggestions || []);
    }
    renderSuggestions(list) {
      this.suggestionsEl.innerHTML = '';
      list.forEach((s) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lm-ai__chip';
        b.textContent = s;
        b.addEventListener('click', () => this.send(s));
        this.suggestionsEl.appendChild(b);
      });
    }

    addMessage(role, text, opts = {}) {
      const wrap = document.createElement('div');
      wrap.className = `lm-ai__msg lm-ai__msg--${role}`;
      if (role === 'assistant') {
        const avatar = document.createElement('div');
        avatar.className = 'lm-ai__msg-avatar';
        avatar.innerHTML = '✦';
        wrap.appendChild(avatar);
      }
      const bubble = document.createElement('div');
      bubble.className = 'lm-ai__bubble';
      bubble.textContent = text;
      wrap.appendChild(bubble);
      this.messagesEl.appendChild(wrap);
      this.scroll();
      return bubble;
    }
    scroll() { this.messagesEl.scrollTop = this.messagesEl.scrollHeight; }

    setTyping(on) {
      if (on) {
        // Drop any prior indicator first so a re-entrant send() can't orphan
        // it (leaving a permanent stuck "typing" bubble).
        if (this.typingEl) { this.typingEl.remove(); this.typingEl = null; }
        this.typingEl = document.createElement('div');
        this.typingEl.className = 'lm-ai__msg lm-ai__msg--assistant';
        this.typingEl.innerHTML = '<div class="lm-ai__msg-avatar">✦</div><div class="lm-ai__bubble lm-ai__typing"><span></span><span></span><span></span></div>';
        this.messagesEl.appendChild(this.typingEl);
        this.scroll();
      } else if (this.typingEl) { this.typingEl.remove(); this.typingEl = null; }
    }

    async send(text) {
      text = (text || '').trim();
      if (!text) return;
      this.suggestionsEl.innerHTML = '';
      this.addMessage('user', text);
      this.history.push({ role: 'user', content: text });
      this.input.value = '';
      this.input.style.height = 'auto';
      this.setTyping(true);
      try {
        const result = await this.respond(text);
        this.setTyping(false);
        if (result.reply) { this.addMessage('assistant', result.reply); this.history.push({ role: 'assistant', content: result.reply }); }
        if (this.recommend !== false && result.products && result.products.length) this.renderProducts(result.products);
        if (result.suggestions) this.renderSuggestions(result.suggestions);
      } catch (err) {
        this.setTyping(false);
        // Drop the unanswered turn so history never accumulates dangling
        // user messages across failures.
        if (this.history.length && this.history[this.history.length - 1].role === 'user') this.history.pop();
        this.addMessage('assistant', utils.t('aiError', "I couldn't reach the assistant right now. Let me show you some popular picks instead."));
        const fb = (this.lastContext && this.lastContext.products && this.lastContext.products.length)
          ? this.lastContext.products
          : await this.searchProducts(text);
        if (fb.length) this.renderProducts(fb);
      }
    }

    // Cap what we send upstream: long chats otherwise grow input cost
    // (roughly quadratically over a session) with no quality benefit.
    recentHistory() {
      return this.history.slice(-12).map((m) => ({ role: m.role, content: m.content }));
    }

    async respond(text) {
      const context = await this.buildContext(text);
      if (this.provider === 'proxy' && this.proxyUrl) {
        const data = await utils.fetchJSON(this.proxyUrl, {
          method: 'POST',
          body: JSON.stringify({ messages: this.recentHistory(), system: this.cfg.system, model: this.model, context }),
        });
        return { reply: data.reply || data.message || '', products: data.products || context.products };
      }
      if ((this.provider === 'anthropic' || this.provider === 'openai')) {
        const key = utils.storage.get('lumina:ai_key');
        if (key) return this.directLLM(text, context, key);
      }
      // Rule-based, catalog-grounded assistant.
      return this.ruleBased(text, context);
    }

    // Direct browser LLM call — DEV/EVALUATION ONLY (key in localStorage).
    async directLLM(text, context, key) {
      const grounding = context.products.map((p) => `- ${p.title} (${p.price}) ${p.url}`).join('\n');
      const sys = `${this.cfg.system}\nOnly recommend from these real products:\n${grounding || '(none found)'}\nKeep replies under 90 words.`;
      if (this.provider === 'anthropic') {
        const res = await utils.fetchJSON('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: { 'x-api-key': key, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
          body: JSON.stringify({ model: this.model || 'claude-haiku-4-5', max_tokens: 400, system: sys, messages: this.recentHistory() }),
        });
        return { reply: (res.content && res.content[0] && res.content[0].text) || '', products: context.products };
      }
      const res = await utils.fetchJSON('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + key },
        body: JSON.stringify({ model: this.model || 'gpt-4o-mini', max_tokens: 400, messages: [{ role: 'system', content: sys }, ...this.recentHistory()] }),
      });
      return { reply: res.choices?.[0]?.message?.content || '', products: context.products };
    }

    async buildContext(text) {
      const products = await this.searchProducts(text);
      this.lastContext = {
        products,
        page: { type: document.body.className, url: location.pathname, title: document.title },
      };
      return this.lastContext;
    }

    // Ground every recommendation in real store products.
    async searchProducts(query) {
      if (this.recommend === false) return [];
      try {
        const terms = this.extractTerms(query);
        const params = new URLSearchParams({ q: terms, 'resources[type]': 'product', 'resources[limit]': '4' });
        // JSON flavor lives at <route>.json; results sit under resources.results.
        const data = await utils.fetchJSON(config.routes.predictiveSearch + '.json?' + params.toString());
        const results = (data.resources && data.resources.results) || {};
        return (results.products || []).map((p) => {
          let image = '';
          if (p.featured_image && p.featured_image.url) {
            try {
              const u = new URL(p.featured_image.url, window.location.origin);
              u.searchParams.set('width', '160');
              image = u.toString();
            } catch (_) { image = p.featured_image.url; }
          }
          return {
            title: p.title,
            url: p.url,
            price: p.price != null && p.price !== '' ? utils.formatMoney(Math.round(Number(p.price) * 100)) : '',
            image,
          };
        });
      } catch (_) { return []; }
    }
    extractTerms(text) {
      const stop = new Set(['the', 'a', 'an', 'for', 'me', 'i', 'need', 'want', 'find', 'best', 'good', 'show', 'recommend', 'looking', 'some', 'any', 'to', 'of', 'and', 'with', 'how', 'do', 'what', 'is', 'are', 'can', 'you', 'my', 'please']);
      return text.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/).filter((w) => w && !stop.has(w)).slice(0, 6).join(' ') || text;
    }

    ruleBased(text, context) {
      const t = text.toLowerCase();
      const found = context.products.length > 0;
      let reply = null;
      let products = null;
      let suggestions = null;

      const INTENTS = [
        {
          re: /^(hi|hello|hey|good (morning|afternoon|evening)|yo)\b/,
          reply: `Hi! I'm ${this.name} 👋 Tell me what you're working on — studying, budgeting, planning or running a business — and I'll search the catalog. You can also ask how to check delivery, compatibility or license details.`,
          products: [],
        },
        {
          re: /thank|thanks|thx|bye|goodbye|that('s| is) all/,
          reply: "You're welcome! If anything else comes up — products, downloads, licenses — I'm right here. Happy building! ✦",
          products: [],
        },
        {
          re: /human|real person|support|contact|talk to (someone|somebody)|customer service|email you/,
          reply: 'Of course — use the Contact link in the footer and include your order number if your question concerns a purchase.',
          products: [],
        },
        {
          re: /download|deliver|receive|access my|where.*files?|how.*get|after.*(buy|purchase|checkout)/,
          reply: 'Delivery can vary by product. Check the delivery details on its page, then follow the instructions shown after checkout and in your order email.',
        },
        {
          re: /offline|internet|wifi|connection|mac|windows|chromebook|ipad|tablet|phone|android|iphone|compatib|requirement|install/,
          reply: 'Compatibility varies by product. Check its description for supported software, devices, file formats and internet requirements before purchase.',
        },
        {
          re: /format|file type|what.*included|xlsx|excel|pdf|what do i get/,
          reply: 'Included formats vary by product. Open the product page and review its description, digital details and “What’s included” section.',
        },
        {
          re: /update|lifetime|new version|latest/,
          reply: 'Update access varies by product. Check the product description and included terms for the exact policy.',
        },
        {
          re: /license|commercial|personal use|client work|resell|team/,
          reply: 'License options and usage rights vary by product. Review the available option names and license terms on the product page; contact support if your intended use is not clearly covered.',
        },
        {
          re: /refund|return|money.?back|guarantee|wrong file|broken|not working|corrupt/,
          reply: 'Review the refund policy linked in the footer before purchase. If a file is not working as described, contact support with your order number and the issue you found.',
        },
        {
          re: /discount|coupon|promo|student.*(discount|price|off)|cheap|sale|deal/,
          reply: 'Active offers show their eligibility, discount and end date on the storefront. If you have a code, enter it at checkout and confirm the total before paying.',
        },
        {
          re: /pay|payment|card|paypal|shop pay|checkout secure|safe to buy/,
          reply: 'Checkout is processed by Shopify. The payment methods currently available to you are shown during checkout before you place the order.',
        },
        {
          re: /gift card|gift certificate|voucher/,
          reply: 'Gift-card availability can change. Search the catalog for “gift card” or contact support to confirm before relying on one.',
        },
      ];

      for (const intent of INTENTS) {
        if (intent.re.test(t)) {
          reply = intent.reply;
          if (intent.products) products = intent.products;
          break;
        }
      }

      // Audience/topic aware phrasing when we DID match products.
      if (!reply && found) {
        const TOPICS = [
          [/student|study|exam|class|school|college|university|gpa|assignment/, 'These catalog results may fit a study workflow:'],
          [/budget|money|finance|debt|saving|expense/, 'These catalog results relate to budgeting and finance:'],
          [/plan|planner|organize|organis|schedule|calendar|habit/, 'These planners and trackers match your search:'],
          [/business|freelance|client|invoice|startup|sop/, 'These catalog results relate to business workflows:'],
          [/ai|prompt|gpt|automat/, 'For AI workflows, start with these:'],
          [/wedding|event/, 'These catalog results relate to event planning:'],
        ];
        const hit = TOPICS.find(([re]) => re.test(t));
        reply = hit ? hit[1] : `Here's what matches "${text}" in our catalog:`;
      }

      if (!reply) {
        reply = found
          ? `Here's what matches "${text}" in our catalog:`
          : "I couldn't find a direct match. Try a keyword like 'budget', 'planner', 'resume', 'flashcards' or 'business plan' — or open Shop by Goal in the menu to browse by what you're trying to achieve.";
      }

      if (products === null) products = context.products;
      if (!suggestions) {
        suggestions = products.length
          ? ['How do I check delivery?', 'How do I check the license?', 'Are there active offers?']
          : this.cfg.suggestions && this.cfg.suggestions.length
            ? this.cfg.suggestions
            : ['Show me study tools', 'Help me budget', 'How do downloads work?'];
      }
      return { reply, products, suggestions };
    }

    // Resolve untrusted (proxy/LLM) product URLs against our origin and allow
    // only http/https/mailto. Using the URL parser defeats whitespace/control-
    // character obfuscation (e.g. "  java\tscript:") that a ^scheme regex misses.
    safeUrl(url) {
      try {
        const u = new URL(url, location.origin);
        return (u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:') ? u.href : '#';
      } catch (_) { return '#'; }
    }

    renderProducts(products) {
      const wrap = document.createElement('div');
      wrap.className = 'lm-ai__products';
      products.forEach((p) => {
        const a = document.createElement('a');
        a.className = 'lm-ai__product';
        a.href = this.safeUrl(p.url);
        a.innerHTML = '<span class="lm-ai__product-media"></span><span class="lm-ai__product-info"><span class="lm-ai__product-title"></span><span class="lm-ai__product-price"></span></span>';
        // Product data can come from a proxy/LLM response — build the image
        // via DOM APIs (never string-interpolated HTML) with a scheme check.
        const media = a.querySelector('.lm-ai__product-media');
        if (p.image && !/^(javascript|data|vbscript):/i.test(p.image)) {
          const img = document.createElement('img');
          img.src = p.image;
          img.alt = '';
          img.width = 48;
          img.height = 48;
          img.loading = 'lazy';
          media.appendChild(img);
        } else {
          media.innerHTML = '<span class="lm-placeholder"></span>';
        }
        a.querySelector('.lm-ai__product-title').textContent = p.title;
        a.querySelector('.lm-ai__product-price').textContent = p.price;
        wrap.appendChild(a);
      });
      this.messagesEl.appendChild(wrap);
      this.scroll();
    }
  }
  if (!customElements.get('lumina-ai')) customElements.define('lumina-ai', LuminaAI);
})();
